import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, FlaskConical, ShieldCheck, MessageSquare } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAdminApi, btnPrimary, cardCls } from "./useAdminApi";
import { ReasonsPanel } from "./ReasonsPanel";
import { API, errMsg } from "@/lib/api";
import { useAdminApi as useApi } from "./useAdminApi";

const WEBHOOK_URL = `${API}/razorpay/webhook`;

const WhatsAppCard = ({ configured }) => {
  const call = useApi();
  const [busy, setBusy] = useState(false);
  const sendNow = async () => {
    setBusy(true);
    try { const r = await call("post", "/admin/reminders/send"); toast.success(`Sending reminders for ${r.target_date}`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className={cardCls} data-testid="whatsapp-reminders-card">
      <h3 className="text-xl font-semibold">WhatsApp reminders</h3>
      <p className={`text-sm mt-2 ${configured ? "text-emerald-700" : "text-ink-muted"}`} data-testid="whatsapp-status">
        {configured ? "On — every evening at 7:00 PM, patients booked for the next day get a WhatsApp reminder." : "Off — add the Twilio WhatsApp sender number to turn on evening reminders."}
      </p>
      {configured && <button className={`${btnPrimary} mt-4`} onClick={sendNow} disabled={busy} data-testid="send-reminders-now">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Send tomorrow's reminders now</button>}
    </div>
  );
};

const ModeOption = ({ active, onClick, icon: I, title, text, testid, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
    className={`text-left rounded-2xl border p-4 transition-colors disabled:opacity-50 ${active ? "border-teal bg-mint" : "border-slate-200 hover:bg-slate-50"}`}>
    <I className="h-5 w-5 text-teal" /><p className="font-semibold mt-2 text-sm">{title}</p><p className="text-xs text-ink-muted mt-1">{text}</p>
  </button>
);

export const SettingsPanel = () => {
  const call = useAdminApi();
  const [s, setS] = useState(null);
  const [fee, setFee] = useState("");
  const [busy, setBusy] = useState(false);
  const [reasonsKey, setReasonsKey] = useState(0);
  useEffect(() => { call("get", "/admin/settings").then((d) => { setS(d); setFee(String(d.consultation_fee)); }); }, [call]);

  const save = async (body, msg) => {
    setBusy(true);
    try { setS(await call("put", "/admin/settings", body)); toast.success(msg); setReasonsKey((k) => k + 1); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!s) return <div className={`${cardCls} mt-4`}><Loader2 className="h-5 w-5 animate-spin" /></div>;

  return (
    <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-4 mt-4" data-testid="settings-panel">
      <div className="space-y-4">
        <div className={cardCls}>
          <h3 className="text-xl font-semibold">Consultation fee</h3>
          <p className="text-sm text-ink-muted mt-1">Applies to every visit reason and the whole website.</p>
          <div className="flex gap-2 mt-4">
            <div className="flex h-10 rounded-xl border border-input overflow-hidden flex-1"><span className="px-3 grid place-items-center bg-slate-50 text-sm">₹</span>
              <input type="number" min="0" value={fee} onChange={(e) => setFee(e.target.value)} className="flex-1 px-3 text-sm outline-none" data-testid="fee-input" /></div>
            <button className={btnPrimary} disabled={busy || fee === ""} onClick={() => save({ consultation_fee: Number(fee) }, "Fee updated")} data-testid="fee-save-button">Save</button>
          </div>
        </div>
        <div className={cardCls}>
          <h3 className="text-xl font-semibold">Payment mode</h3>
          <div className="grid grid-cols-2 gap-2 mt-4">
            <ModeOption active={s.razorpay_mode === "sandbox"} onClick={() => save({ razorpay_mode: "sandbox" }, "Demo payments on")} icon={FlaskConical} title="Sandbox / Demo" text="One-click test payments, no money moves" testid="mode-sandbox" />
            <ModeOption active={s.razorpay_mode === "live"} onClick={() => save({ razorpay_mode: "live" }, "Live Razorpay payments on")} icon={ShieldCheck} title="Live Razorpay" text="Real UPI, card & netbanking payments" testid="mode-live" />
          </div>
          <p className={`text-xs mt-3 ${s.live_keys_configured ? "text-emerald-700" : "text-amber-700"}`} data-testid="razorpay-keys-status">
            {s.live_keys_configured ? "Razorpay keys are connected." : "Razorpay keys not added yet — live mode stays off until they are."}
          </p>
          <p className="text-xs mt-1 text-ink-muted" data-testid="effective-mode">Patients currently see: {s.effective_payment_mode === "live" ? "Razorpay checkout" : "Demo checkout"}</p>
          <div className="mt-4 rounded-2xl bg-alabaster border border-slate-200 p-3" data-testid="webhook-info">
            <p className="text-xs font-semibold">Payment webhook {s.webhook_configured ? <span className="text-emerald-700">· connected</span> : <span className="text-amber-700">· secret not added</span>}</p>
            <p className="text-[11px] text-ink-muted mt-1">Razorpay Dashboard → Webhooks → add this URL with events payment.captured and order.paid:</p>
            <code className="block mt-1 text-[11px] break-all bg-white border rounded-lg px-2 py-1" data-testid="webhook-url">{WEBHOOK_URL}</code>
          </div>
        </div>
        <div className={cardCls}>
          <h3 className="text-xl font-semibold flex items-center gap-2"><MessageSquare className="h-5 w-5 text-teal" />SMS confirmations</h3>
          <p className={`text-sm mt-2 ${s.sms_configured ? "text-emerald-700" : "text-ink-muted"}`} data-testid="sms-status">
            {s.sms_configured ? "On — patients and the clinic get an SMS for every confirmed booking." : "Off — add Twilio keys to send booking SMS to patients and the clinic."}
          </p>
        </div>
        <WhatsAppCard configured={s.whatsapp_configured} />
      </div>
      <ReasonsPanel key={reasonsKey} />
    </div>
  );
};
